// Analytics utility functions - Multi-platform support
import ReactGA from 'react-ga4';
import posthog from 'posthog-js';

// Type definitions
interface AnalyticsEventData {
  [key: string]: string | number | boolean | undefined;
}

// Global type declarations
declare global {
  interface Window {
    umami?: {
      track: (eventName: string, eventData?: Record<string, any>) => void;
    };
    gtag?: (command: string, targetId: string, config?: any) => void;
  }
}

// Initialize analytics platforms
export const initializeAnalytics = () => {
  // Initialize Google Analytics 4
  const GA4_MEASUREMENT_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID;
  console.log('GA4 Measurement ID:', GA4_MEASUREMENT_ID);
  if (GA4_MEASUREMENT_ID) {
    // Check if we're in debug mode
    const isDebugMode = window.location.search.includes('debug_mode=1') || 
                       window.location.search.includes('debug_mode=true');
    
    ReactGA.initialize(GA4_MEASUREMENT_ID, {
      testMode: isDebugMode,
      gaOptions: {
        debug_mode: isDebugMode ? 1 : 0
      }
    });
    console.log('Google Analytics 4 initialized with ID:', GA4_MEASUREMENT_ID, 'Debug mode:', isDebugMode);
    
    // Send a test event to help GA4 discover custom events
    ReactGA.event('app_initialized', { 
      app_name: 'DoseWise',
      version: '1.0.0',
      environment: process.env.NODE_ENV,
      debug_mode: isDebugMode
    });
  } else {
    console.warn('GA4 Measurement ID not found in environment variables');
  }

  // Initialize PostHog
  const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY;
  const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://eu.i.posthog.com';
  
  console.log('PostHog Configuration:', {
    key: POSTHOG_KEY ? 'SET' : 'NOT SET',
    keyValue: POSTHOG_KEY ? `${POSTHOG_KEY.substring(0, 10)}...` : 'undefined',
    host: POSTHOG_HOST,
    environment: process.env.NODE_ENV,
    allEnvVars: {
      POSTHOG_KEY: process.env.REACT_APP_POSTHOG_KEY ? 'SET' : 'NOT SET',
      POSTHOG_HOST: process.env.REACT_APP_POSTHOG_HOST ? 'SET' : 'NOT SET'
    }
  });
  
  if (POSTHOG_KEY && POSTHOG_KEY !== 'phc_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx' && !POSTHOG_KEY.includes('xxxxxxxxx')) {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      // Enable session recordings
      session_recording: {
        maskAllInputs: true,
      },
      // Capture pageviews automatically
      capture_pageview: true,
      // Debug mode for development
      loaded: (posthog) => {
        console.log('PostHog initialized successfully with session recordings');
        console.log('PostHog instance:', posthog);
      },
    });
    console.log('PostHog initialization started');
  } else {
    console.warn('PostHog API key not found or is placeholder. Please set REACT_APP_POSTHOG_KEY in Netlify environment variables.');
  }
};

// Track page views
export const trackPageView = (path: string, title?: string) => {
  // Google Analytics 4
  ReactGA.send({ hitType: 'pageview', page: path, title });
  
  // PostHog (automatic if capture_pageview is enabled)
  posthog.capture('$pageview', { $current_url: path, title });
  
  // Umami (if available)
  if (window.umami) {
    window.umami.track('pageview', { path, title });
  }
};

// Track custom events
export const trackEvent = (eventName: string, eventData?: AnalyticsEventData) => {
  // Clean and validate event data for GA4
  const cleanEventData = eventData ? Object.entries(eventData).reduce((acc, [key, value]) => {
    // GA4 parameter name limits: 40 characters
    const cleanKey = key.length > 40 ? key.substring(0, 40) : key;
    
    // GA4 parameter value limits: 100 characters for strings
    let cleanValue = value;
    if (typeof value === 'string' && value.length > 100) {
      cleanValue = value.substring(0, 100);
    }
    
    acc[cleanKey] = cleanValue;
    return acc;
  }, {} as Record<string, any>) : undefined;

  // Google Analytics 4
  ReactGA.event(eventName, cleanEventData);
  
  // PostHog
  posthog.capture(eventName, eventData);
  
  // Umami (existing implementation)
  if (window.umami) {
    window.umami.track(eventName, eventData);
  }
  
  // Debug logging
  if (process.env.NODE_ENV === 'development') {
    console.log('Analytics Event:', eventName, eventData);
    console.log('User ID:', posthog.get_distinct_id());
    console.log('GA4 Event sent:', eventName, cleanEventData);
    
    // Enhanced GA4 debugging
    const isDebugMode = window.location.search.includes('debug_mode=1');
    if (isDebugMode) {
      console.log('🔍 GA4 DEBUG MODE ACTIVE');
      console.log('📊 Event Details:', {
        name: eventName,
        parameters: cleanEventData,
        timestamp: new Date().toISOString(),
        url: window.location.href
      });
    }
  }
};

// Identify user (for PostHog user tracking)
export const identifyUser = (userId: string, userProperties?: Record<string, any>) => {
  console.log('Identifying user in analytics:', userId, 'Properties:', userProperties);
  
  // PostHog user identification
  try {
    posthog.identify(userId, userProperties);
    console.log('PostHog user identification successful');
  } catch (error) {
    console.error('PostHog user identification failed:', error);
  }
  
  // Google Analytics 4 user properties
  try {
    ReactGA.set({ user_id: userId, ...userProperties });
    console.log('GA4 user identification successful');
  } catch (error) {
    console.error('GA4 user identification failed:', error);
  }
  
  console.log('User identification complete');
};

// Track user properties
export const setUserProperties = (properties: Record<string, any>) => {
  // Google Analytics 4
  ReactGA.set(properties);
  
  // PostHog
  posthog.people.set(properties);
};

// DoseWise-specific event tracking functions
export const trackDoseWiseEvents = {
  // User registration and authentication
  userRegistered: (method: string) => {
    trackEvent('user_registered', { method });
    // Also send as a standard GA4 event to help discovery
    ReactGA.event('sign_up', { method });
  },
  userLoggedIn: (method: string) => {
    trackEvent('user_logged_in', { method });
    // Also send as a standard GA4 event to help discovery
    ReactGA.event('login', { method });
  },
  
  // Pen management
  penAdded: (strength: number, costPerPen: number) => 
    trackEvent('pen_added', { pen_strength: strength, cost_per_pen: costPerPen }),
  penActivated: (penId: string, strength: number) => 
    trackEvent('pen_activated', { pen_id: penId, pen_strength: strength }),
  
  // Dose logging
  doseLogged: (volume: number, penStrength: number, isGoldenDose: boolean) => 
    trackEvent('dose_logged', { 
      dose_volume: volume, 
      pen_strength: penStrength, 
      is_golden_dose: isGoldenDose 
    }),
  
  // Golden dose feature
  goldenDoseEnabled: (penId: string) => 
    trackEvent('golden_dose_enabled', { pen_id: penId }),
  goldenDoseExtracted: (penId: string, volume: number, savings: number) => 
    trackEvent('golden_dose_extracted', { 
      pen_id: penId, 
      volume: volume, 
      estimated_savings: savings 
    }),
  
  // Cost tracking
  costSavingsViewed: (totalSavings: number, activePens: number) => 
    trackEvent('cost_savings_viewed', { 
      total_savings: totalSavings, 
      active_pens: activePens 
    }),
  
  // Settings and preferences
  settingsUpdated: (settingType: string, value: any) => 
    trackEvent('settings_updated', { setting_type: settingType, value }),
  
  // Feature usage
  pkCurveViewed: (penStrength: number) => 
    trackEvent('pk_curve_viewed', { pen_strength: penStrength }),
  feedbackSubmitted: (rating: number, hasComment: boolean) => 
    trackEvent('feedback_submitted', { rating, has_comment: hasComment }),

  // Landing page engagement
  landingPageViewed: (source?: string) =>
    trackEvent('landing_page_viewed', { source }),
  calculatorInteracted: (penStrength: number) =>
    trackEvent('calculator_interacted', { pen_strength: penStrength }),
  ctaClicked: (ctaId: string, position: string) =>
    trackEvent('cta_clicked', { cta_id: ctaId, position }),
  educationalSectionExpanded: (sectionId: string) =>
    trackEvent('educational_section_expanded', { section_id: sectionId }),
  externalLinkClicked: (linkId: string) =>
    trackEvent('external_link_clicked', { link_id: linkId }),
  adVisible: (slot: string) =>
    trackEvent('ad_visible', { slot }),
};

// Feature flag helper (PostHog)
export const getFeatureFlag = (flagKey: string, defaultValue: boolean = false): boolean => {
  return posthog.isFeatureEnabled(flagKey) ?? defaultValue;
};

// Session recording control
export const startSessionRecording = () => {
  posthog.startSessionRecording();
};

export const stopSessionRecording = () => {
  posthog.stopSessionRecording();
};

// Privacy controls
export const optOutOfAnalytics = () => {
  posthog.opt_out_capturing();
  // Add GA4 opt-out if needed
  if (window.gtag && process.env.REACT_APP_GA4_MEASUREMENT_ID) {
    window.gtag('config', process.env.REACT_APP_GA4_MEASUREMENT_ID, {
      send_page_view: false
    });
  }
};

export const optInToAnalytics = () => {
  posthog.opt_in_capturing();
}; 